import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { campaignsAPI } from '../../../services/leadGenAPI';

const CampaignsList = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    leadType: '',
    crmCampaign: '',
    dateRange: [null, null],
    costRange: [0, 1000],
    searchTerm: '',
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [campaigns, filters]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await campaignsAPI.getCampaigns();
      setCampaigns(response.results || response);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...campaigns];

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(campaign => campaign.status === filters.status);
    }

    // Lead type filter
    if (filters.leadType) {
      filtered = filtered.filter(campaign => campaign.lead_type === filters.leadType);
    }

    // CRM campaign filter
    if (filters.crmCampaign) {
      filtered = filtered.filter(campaign => 
        campaign.crm_campaign.toLowerCase().includes(filters.crmCampaign.toLowerCase())
      );
    }

    // Date range filter
    if (filters.dateRange[0] && filters.dateRange[1]) {
      filtered = filtered.filter(campaign => {
        if (!campaign.last_generated) return false;
        const generatedDate = moment(campaign.last_generated);
        return generatedDate.isBetween(filters.dateRange[0], filters.dateRange[1], 'day', '[]');
      });
    }

    // Cost range filter
    filtered = filtered.filter(campaign => 
      campaign.total_cost >= filters.costRange[0] && campaign.total_cost <= filters.costRange[1]
    );

    // Search term filter
    if (filters.searchTerm) {
      filtered = filtered.filter(campaign =>
        campaign.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        campaign.crm_campaign.toLowerCase().includes(filters.searchTerm.toLowerCase())
      );
    }

    setFilteredCampaigns(filtered);
    setPage(0);
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      let response;
      switch (newStatus) {
        case 'active':
          response = await campaignsAPI.resumeCampaign(campaignId);
          break;
        case 'paused':
          response = await campaignsAPI.pauseCampaign(campaignId);
          break;
        case 'cancelled':
          response = await campaignsAPI.cancelCampaign(campaignId);
          break;
        default:
          throw new Error(`Invalid status: ${newStatus}`);
      }
      
      // Update local state with the response
      setCampaigns(prev => prev.map(campaign =>
        campaign.id === campaignId ? { ...campaign, ...response } : campaign
      ));
    } catch (error) {
      console.error('Error updating campaign status:', error);
      // Revert the change if API call fails
      fetchCampaigns();
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      await campaignsAPI.deleteCampaign(selectedCampaign.id);
      setCampaigns(prev => prev.filter(campaign => campaign.id !== selectedCampaign.id));
      setDeleteDialogOpen(false);
      setSelectedCampaign(null);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      // Refresh the list if deletion fails
      fetchCampaigns();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'draft': return 'default';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <PlayIcon />;
      case 'paused': return <PauseIcon />;
      case 'draft': return <EditIcon />;
      case 'completed': return <StopIcon />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return moment(dateString).format('MMM DD, YYYY HH:mm');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" component="h1" color="text.primary">
              Lead Generation Campaigns
            </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/lead-gen/campaigns/create')}
        >
          Create Campaign
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="text.primary">
              Filters
            </Typography>
            <Box>
              <Button
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ mr: 1 }}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
              <Button
                startIcon={<ClearIcon />}
                onClick={() => setFilters({
                  status: '',
                  leadType: '',
                  crmCampaign: '',
                  dateRange: [null, null],
                  costRange: [0, 1000],
                  searchTerm: '',
                })}
              >
                Clear
              </Button>
            </Box>
          </Box>

          {showFilters && (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Search Campaigns"
                    value={filters.searchTerm}
                    onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                    placeholder="Search by name or CRM campaign..."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="paused">Paused</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Lead Type</InputLabel>
                    <Select
                      value={filters.leadType}
                      label="Lead Type"
                      onChange={(e) => setFilters(prev => ({ ...prev, leadType: e.target.value }))}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="B2B">B2B</MenuItem>
                      <MenuItem value="D2C">D2C</MenuItem>
                      <MenuItem value="Mixed">Mixed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="CRM Campaign"
                    value={filters.crmCampaign}
                    onChange={(e) => setFilters(prev => ({ ...prev, crmCampaign: e.target.value }))}
                    placeholder="Filter by CRM campaign..."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="From Date"
                    value={filters.dateRange[0]}
                    onChange={(date) => setFilters(prev => ({ ...prev, dateRange: [date, prev.dateRange[1]] }))}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="To Date"
                    value={filters.dateRange[1]}
                    onChange={(date) => setFilters(prev => ({ ...prev, dateRange: [prev.dateRange[0], date] }))}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography gutterBottom>Cost Range: ${filters.costRange[0]} - ${filters.costRange[1]}</Typography>
                  <Slider
                    value={filters.costRange}
                    onChange={(event, newValue) => setFilters(prev => ({ ...prev, costRange: newValue }))}
                    valueLabelDisplay="auto"
                    min={0}
                    max={10000}
                    step={100}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          )}
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Campaign Name</TableCell>
                <TableCell>CRM Campaign</TableCell>
                <TableCell>Lead Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Target / Achieved</TableCell>
                <TableCell>Primary Source</TableCell>
                <TableCell>Cost (Total/Per Lead)</TableCell>
                <TableCell>Last Generated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : filteredCampaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No campaigns found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCampaigns
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{campaign.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {campaign.crm_campaign}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={campaign.lead_type} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(campaign.status)}
                          label={campaign.status}
                          color={getStatusColor(campaign.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {campaign.achieved_count} / {campaign.target_count}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(campaign.achieved_count / campaign.target_count) * 100}
                            sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {campaign.primary_source}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {formatCurrency(campaign.total_cost)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatCurrency(campaign.cost_per_lead)} per lead
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(campaign.last_generated)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/lead-gen/campaigns/${campaign.id}`)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Campaign">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/lead-gen/campaigns/${campaign.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {campaign.status === 'active' ? (
                            <Tooltip title="Pause Campaign">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleStatusChange(campaign.id, 'paused')}
                              >
                                <PauseIcon />
                              </IconButton>
                            </Tooltip>
                          ) : campaign.status === 'paused' ? (
                            <Tooltip title="Resume Campaign">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleStatusChange(campaign.id, 'active')}
                              >
                                <PlayIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Delete Campaign">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedCampaign(campaign);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredCampaigns.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedCampaign?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCampaign} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignsList; 